import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { VanguardNav } from '../components/vanguard/VanguardNav';
import { ServiceHero } from '../components/vanguard/ServiceHero';
import { ServiceContent } from '../components/vanguard/ServiceContent';
import { ServiceSidebar } from '../components/vanguard/ServiceSidebar';
import { LandingFooter } from '../components/landing/LandingFooter';
import { PageTransition } from '../components/common/PageTransition';
import { servicesData } from '../data/services';
import { useEffect } from 'react';

export const ServicePage = () => {
    const { slug } = useParams();
    const service = slug ? servicesData[slug] : null;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!service) {
        return <Navigate to="/" replace />;
    }

    // Estrutura JSON-LD para LegalService (SEO Rich Snippets)
    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "LegalService",
        "name": `Pacces Advogados - ${service.hero.subtitle}`,
        "description": service.seo.description,
        "url": `https://paccesadvogados.com.br/areas/${service.slug}`,
        "telephone": "+551123690371",
        "image": service.hero.image,
        "priceRange": "$$$",
        "areaServed": [
            { "@type": "City", "name": "São Paulo" },
            { "@type": "State", "name": "São Paulo" }
        ],
        "memberOf": {
            "@type": "Organization",
            "name": "Pacces Advogados"
        },
        "address": [
            {
                "@type": "PostalAddress",
                "streetAddress": "Av. Ordem e Progresso, 157, Sala 408",
                "addressLocality": "São Paulo",
                "addressRegion": "SP",
                "postalCode": "01141-030",
                "addressCountry": "BR"
            },
        ],
        "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
        }
    };

    // FAQPage (GEO/AEO): reaproveita as perguntas já existentes na área, quando houver.
    const faqItems = service.specializedAreas.items.flatMap((i) => i.faq ?? []);
    const faqSchema = faqItems.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqItems.map((f) => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": { "@type": "Answer", "text": f.answer },
        })),
    } : null;

    return (
        <PageTransition>
        <div className="bg-white dark:bg-vanguard-background-dark text-stone-900 dark:text-stone-300 transition-colors duration-300 font-vanguard-sans">
            {/* Metadados Dinâmicos (SEO Strong) */}
            <Helmet>
                <title>{service.seo.title}</title>
                <meta name="description" content={service.seo.description} />
                <meta name="keywords" content={service.seo.keywords} />
                <link rel="canonical" href={`https://paccesadvogados.com.br/areas/${service.slug}`} />

                {/* Otimização de LCP: Preload da imagem da Hero */}
                <link rel="preload" as="image" href={service.hero.image} />

                <meta property="og:title" content={service.seo.title} />
                <meta property="og:description" content={service.seo.description} />
                <meta property="og:image" content={service.hero.image} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://paccesadvogados.com.br/areas/${service.slug}`} />

                <script type="application/ld+json">
                    {JSON.stringify(schemaMarkup)}
                </script>

                {faqSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(faqSchema)}
                    </script>
                )}
            </Helmet>

            <VanguardNav />
            <ServiceHero data={service.hero} />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
                    <ServiceContent data={service} />
                    <ServiceSidebar />
                </div>
            </main>

            <LandingFooter />
        </div>
        </PageTransition>
    );
};
